import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrash, faPlus, faFileExcel, faFilePdf } from "@fortawesome/free-solid-svg-icons";
import MUITable from "../../../Common/MUITable";
import isGHMC, { getCurrentDate } from "../../../Common/helper/helpar";
import { AddAttendanceModal } from "./AddAttendanceModal";
import AttendancePunchHistoryTable from "./AttendancePunchHistoryTable";
import Select from "react-select";

import {
    GetAttendanceZones,
    GetAttendanceWards,
    GetAttendanceShifts,
    GetAttendanceDesignations,
    ManageAttendance,
    DeleteAttendanceRecord,
    GetWardsByZone
} from "../AttendanceApis";
import { toast } from "react-toastify";

import {
    ExportAttendanceExcel,
    ExportAttendancePDF,
    MANAGE_ATTENDANCE_EXPORT_COLUMNS,
} from "./AttendanceExports";
import {
    getTodayDate,
    validateAttendanceDateFilters,
} from "./attendanceReportDateUtils";

const applyButtonStyle = {
    backgroundColor: "#003366",
    color: "#FFFFFF",
    border: "none",
    fontWeight: 600,
    padding: "8px 24px",
    borderRadius: "6px",
    fontSize: "14px",
};

const resetButtonStyle = {
    backgroundColor: "#FDE8E8",
    color: "#8B1A1A",
    border: "none",
    fontWeight: 600,
    padding: "8px 24px",
    borderRadius: "6px",
    fontSize: "14px",
};

const AttendanceManagementInformation = () => {
    const isProd = isGHMC();

    // ─── State ────────────────────────────────────────────────────────────────
    const [attendanceData, setAttendanceData] = useState([]);
    const [zoneOptions, setZoneOptions] = useState([]);
    const [wardOptions, setWardOptions] = useState([]);
    const [shiftOptions, setShiftOptions] = useState([]);
    const [designationOptions, setDesignationOptions] = useState([]);

    const [isLoading, setIsLoading] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [editAttendanceData, setEditAttendanceData] = useState(null);
    const [isEditAttendance, setIsEditAttendance] = useState(false);

    const [filterData, setFilterData] = useState({
        ZoneId: "",
        WardId: "",
        EmployeeSearch: "",
        Status: "",
        ShiftId: "",
        DesignationId: "",
        FromDate: "",
        ToDate: "",
    });

    // ─── Permissions ──────────────────────────────────────────────────────────
    // let storedPermissions = localStorage.getItem("permissionId");
    // const permissionValues = JSON.parse(localStorage.getItem("rolePermissions")) || {};
    // let canWriteValue = null;
    // storedPermissions = parseInt(storedPermissions, 10);
    // const matchingPermission = Array.isArray(permissionValues)
    //     ? permissionValues.find((m) => m.permissionId === storedPermissions)
    //     : null;
    // if (matchingPermission) canWriteValue = matchingPermission.canWrite;

    // ─── Fetch Dropdowns ──────────────────────────────────────────────────────
    const fetchDropdowns = async () => {
        setIsLoading(true);
        try {
            const [zones, wards, shifts, designations] = await Promise.all([
                GetAttendanceZones(),
                GetAttendanceWards(),
                GetAttendanceShifts(),
                GetAttendanceDesignations(),
            ]);
            const rawZones = Array.isArray(zones) ? zones : zones?.data ?? [];
            const delhiZones = rawZones.filter(
                (zone) =>
                    zone.isActive !== false &&
                    String(zone.zoneName ?? zone.ZoneName ?? "")
                        .toLowerCase()
                        .includes("delhi")
            );
            setZoneOptions(delhiZones);
            setWardOptions(Array.isArray(wards) ? wards : wards?.data ?? []);
            setShiftOptions(Array.isArray(shifts) ? shifts : shifts?.data ?? []);
            setDesignationOptions(Array.isArray(designations) ? designations : designations?.data ?? []);
        } catch (error) {
            console.error("Error fetching dropdown data:", error.message);
        } finally {
            setIsLoading(false);
        }
    };

    // ─── Fetch Attendance Records ──────────────────────────────────────────────
const fetchAttendance = async (filters = filterData) => {
    setIsLoading(true);
    try {
        const activeFilters = Object.fromEntries(
            Object.entries(filters).filter(([, v]) => v !== "")
        );

        // Always send pagination explicitly — otherwise the API
        // silently defaults PageSize to 10 on the backend.
        const payload = {
            ...activeFilters,
            PageNumber: 1,
            PageSize: 100, // large enough to fetch "all" records; adjust as needed
        };

        const response = await ManageAttendance(payload);

        let data = [];
        if (Array.isArray(response)) {
            data = response;
        } else if (Array.isArray(response?.data)) {
            data = response.data;       // camelCase-serialized "Data" from API
        } else if (Array.isArray(response?.Data)) {
            data = response.Data;       // fallback if PascalCase comes through
        } else if (Array.isArray(response?.result)) {
            data = response.result;
        }

        setAttendanceData(data);
    } catch (error) {
        console.error("Error fetching attendance data:", error?.message ?? error);
        setAttendanceData([]);
    } finally {
        setIsLoading(false);
    }
};;

    const handleApplyFilters = (e) => {
        e.preventDefault();
        const dateValidationMessage = validateAttendanceDateFilters(filterData);
        if (dateValidationMessage) {
            toast.error(dateValidationMessage);
            return;
        }
        fetchAttendance(filterData);
    };

    const displayedAttendanceData = (Array.isArray(attendanceData) ? attendanceData : []).filter((row) => {
        const search = filterData.EmployeeSearch?.trim().toLowerCase();
        if (search) {
            const found = [row.employeeName, row.empId, row.empCode]
                .filter(Boolean)
                .some((value) => String(value).toLowerCase().includes(search));
            if (!found) return false;
        }

        if (filterData.ZoneId) {
            const rowZoneId = row.zoneId ?? row.ZoneId ?? row.ZoneId;
            if (rowZoneId) {
                if (String(rowZoneId) !== String(filterData.ZoneId)) {
                    return false;
                }
            } else {
                const selectedZone = zoneOptions.find((z) => String(z.zoneId ?? z.ZoneId) === String(filterData.ZoneId));
                const rowZoneName = row.zoneName ?? row.ZoneName ?? row.zone ?? "";
                const selectedZoneName = selectedZone?.zoneName ?? selectedZone?.ZoneName ?? "";
                if (selectedZone && String(rowZoneName).toLowerCase() !== String(selectedZoneName).toLowerCase()) {
                    return false;
                }
            }
        }

        if (filterData.WardId) {
            const rowWardId = row.circleId ?? row.wardId ?? row.WardId ?? row.CircleId;
            if (rowWardId) {
                if (String(rowWardId) !== String(filterData.WardId)) {
                    return false;
                }
            } else {
                const selectedWard = wardOptions.find((w) => String(w.circleId ?? w.wardId ?? w.CircleId) === String(filterData.WardId));
                const rowWardName = row.circleName ?? row.wardName ?? row.wardNo ?? "";
                const selectedWardName = selectedWard?.circleName ?? selectedWard?.wardName ?? selectedWard?.wardNo ?? "";
                if (selectedWard && String(rowWardName).toLowerCase() !== String(selectedWardName).toLowerCase()) {
                    return false;
                }
            }
        }

        if (filterData.Status) {
            const rowStatus = String(row.status ?? row.Status ?? "").toLowerCase();
            if (rowStatus !== String(filterData.Status).toLowerCase()) {
                return false;
            }
        }

        if (filterData.ShiftId) {
            const rowShiftId = row.shiftId ?? row.ShiftId;
            if (rowShiftId) {
                if (String(rowShiftId) !== String(filterData.ShiftId)) {
                    return false;
                }
            } else {
                const rowShiftName = row.actualShift ?? row.masterShift ?? row.shiftName ?? "";
                const selectedShift = shiftOptions.find((s) => String(s.shiftId) === String(filterData.ShiftId));
                if (selectedShift && String(rowShiftName).toLowerCase() !== String(selectedShift.shiftName ?? "").toLowerCase()) {
                    return false;
                }
            }
        }

        if (filterData.DesignationId) {
            const rowDesignationId = row.designationId ?? row.DesignationId ?? row.designation?.designationId;
            if (rowDesignationId) {
                if (String(rowDesignationId) !== String(filterData.DesignationId)) {
                    return false;
                }
            } else {
                const rowDesignationName = row.designation ?? row.designationName ?? row.Designation ?? "";
                const selectedDesignation = designationOptions.find((d) => String(d.designationId) === String(filterData.DesignationId));
                if (selectedDesignation && String(rowDesignationName).toLowerCase() !== String(selectedDesignation.designationName ?? "").toLowerCase()) {
                    return false;
                }
            }
        }

        if (filterData.FromDate && row.date < filterData.FromDate) {
            return false;
        }

        if (filterData.ToDate && row.date > filterData.ToDate) {
            return false;
        }

        return true;
    });

    useEffect(() => {
        fetchDropdowns();
        fetchAttendance();
    }, []);

    useEffect(() => {
        if (zoneOptions.length === 0) return;

        setFilterData((previous) => {
            if (previous.ZoneId) return previous;

            const firstZoneId = String(zoneOptions[0].zoneId ?? zoneOptions[0].ZoneId);

            GetWardsByZone(firstZoneId)
                .then((wards) => {
                    const wardList = Array.isArray(wards) ? wards : wards?.data ?? [];
                    setWardOptions(wardList);
                })
                .catch((error) => {
                    console.error("Ward fetch error:", error);
                    setWardOptions([]);
                });

            return { ...previous, ZoneId: firstZoneId };
        });
    }, [zoneOptions]);

    // ─── Handlers ─────────────────────────────────────────────────────────────
    const handleChange = async (e) => {
        const { name, value } = e.target;

        if (name === "FromDate" || name === "ToDate") {
            const today = getTodayDate();
            if (value && value > today) {
                toast.error(
                    name === "FromDate"
                        ? "From Date cannot be a future date."
                        : "To Date cannot be a future date."
                );
                return;
            }
        }

        setFilterData((prev) => {
            const updated = { ...prev, [name]: value };
            if (name === "ZoneId") updated.WardId = ""; // reset ward when zone changes
            return updated;
        });

        // fetch wards when zone changes
        if (name === "ZoneId") {
            if (value) {
                try {
                    const wards = await GetWardsByZone(value);
                    const wardList = Array.isArray(wards) ? wards : wards?.data ?? [];
                    setWardOptions(wardList);
                } catch (err) {
                    console.error("Ward fetch error:", err);
                    setWardOptions([]);
                }
            } else {
                setWardOptions([]); // clear wards if no zone selected
            }
        }
    };



    const handleReset = () => {
        const cleared = {
            ZoneId: "", WardId: "", EmployeeSearch: "", Status: "",
            ShiftId: "", DesignationId: "", FromDate: "", ToDate: "",
        };
        setFilterData(cleared);
        setWardOptions([]);          // ← clear ward options on reset
        fetchAttendance(cleared);
    };

    // const handleDelete = async (row) => {
    //     if (!window.confirm("Are you sure you want to delete this attendance record?")) return;
    //     setIsLoading(true);
    //     try {
    //         const attendanceId = row.attendanceId ?? row.AttendanceId;
    //         const response = await DeleteAttendanceRecord({ attendanceId });
    //         const success = response?.status === 200 || response?.data?.success || response?.data?.result === 1 || response?.success;
    //         if (success) {
    //             toast.success("Attendance record deleted successfully.");
    //             fetchAttendance();
    //         } else {
    //             toast.error(response?.data?.message || response?.message || "Failed to delete attendance record.");
    //         }
    //     } catch (error) {
    //         toast.error("Error deleting record.");
    //     } finally {
    //         setIsLoading(false);
    //     }
    // };

    const handleExportExcel = async () => {
        const exportData = displayedAttendanceData;
        if (exportData.length === 0) {
            toast.warn("No data to export.");
            return;
        }
        try {
            await ExportAttendanceExcel({
                attendanceData: exportData,
                columns: MANAGE_ATTENDANCE_EXPORT_COLUMNS,
                reportTitle: "Manage Attendance",
                fileName: "ManageAttendanceReport",
            });
        } catch {
            toast.error("Excel export failed.");
        }
    };

    const handleExportPDF = async () => {
        const exportData = displayedAttendanceData;
        if (exportData.length === 0) {
            toast.warn("No data to export.");
            return;
        }
        try {
            await ExportAttendancePDF({
                attendanceData: exportData,
                columns: MANAGE_ATTENDANCE_EXPORT_COLUMNS,
                reportTitle: "Manage Attendance",
                fileName: "ManageAttendanceReport",
            });
        } catch {
            toast.error("PDF export failed.");
        }
    };

    // ─── Status Badge Renderer ─────────────────────────────────────────────────
    const getAttendanceTextValue = (...values) => {
        const found = values.find((value) => value !== undefined && value !== null && value !== "");
        return found ?? "—";
    };

    const expandTableThStyle = {
        backgroundColor: "#003366",
        color: "#fff",
        fontWeight: 600,
        padding: "8px",
        textAlign: "center",
        fontSize: "12px",
    };

    const expandTableTdStyle = {
        backgroundColor: "#f9fafb",
        padding: "8px",
        textAlign: "center",
        fontSize: "12px",
        border: "1px solid #ddd",
    };

    const renderStatus = (row) => {
        const status = row?.status?.toLowerCase();
        const statusMap = {
            present: { label: "Present", bg: "#d1fae5", color: "#065f46" },
            absent: { label: "Absent", bg: "#fee2e2", color: "#991b1b" },
            late: { label: "Late", bg: "#fef3c7", color: "#92400e" },
        };
        const style = statusMap[status] ?? { label: row?.status ?? "—", bg: "#e5e7eb", color: "#374151" };
        return (
            <span
                style={{
                    backgroundColor: style.bg,
                    color: style.color,
                    padding: "3px 12px",
                    borderRadius: "999px",
                    fontWeight: 600,
                    fontSize: "12px",
                    whiteSpace: "nowrap",
                }}
            >
                {style.label}
            </span>
        );
    };

    // ─── Table Columns ────────────────────────────────────────────────────────
    const attendanceColumns = [
        { label: "Emp ID", field: "employeeId" },
        {
            label: "Emp Code",
            render: (row) => getAttendanceTextValue(
                row.employeeCode,
                row.empCode,
                row.EmployeeCode,
                row.empId,
                row.EmployeeId,
                row.employeeId
            ),
        },
        { label: "Employee Name", field: "employeeName" },
        {
            label: "Department",
            render: (row) => getAttendanceTextValue(
                row.departmentName,
                row.DepartmentName,
                row.department,
                row.Department,
                row.operationName,
                row.OperationName
            ),
        },
        {
            label: "Designation",
            render: (row) => getAttendanceTextValue(
                row.designationName,
                row.DesignationName,
                row.designation,
                row.Designation,
                row.roleName,
                row.RoleName
            ),
        },
        { label: "Master Shift", field: "masterShift" },
        { label: "Actual Shift", field: "actualShift" },
        { label: "Zone", field: "zoneName" },
        {
            label: "Ward",
            render: (row) => row.circleName ?? row.wardNo ?? row.wardName ?? "—",
        },
        { label: "Device ID", field: "deviceId" },
        { label: "Date", field: "date" },
        { label: "Punch In", field: "punchIn" },
        { label: "Punch Out", field: "punchOut" },
        { label: "Total Time", field: "totalTime" },
        {
            label: "Status",
            field: "status",
            render: (row) => renderStatus(row),
        },
        {
            label: "Actions",
            render: (row) => (
                <div className="d-flex gap-2 align-items-center">
                    {/* {canWriteValue && ( */}
                    <button
                        className={`${isProd ? "edit-ghmc" : "edit-swm"}`}
                        title="Edit"
                        onClick={() => {
                            setEditAttendanceData(row);
                            setIsEditAttendance(true);
                            setShowAddModal(true);
                        }}
                    >
                        <FontAwesomeIcon icon={faEdit} />
                    </button>
                    {/* )} */}
                    {/* {canWriteValue && ( */}
                    {/* <button
                            className="btn btn-sm btn-danger p-1"
                            title="Delete"
                            style={{ lineHeight: 1 }}
                            onClick={() => handleDelete(row)}
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </button> */}
                    {/* )} */}
                </div>
            ),
        },
    ];

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <>
            <div className="content text-start">
                <div className="page-content">

                    {/* ── Page Header + Actions ── */}
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-3 mt-3 mb-3">
                        <div className="me-md-3">
                            <h5 className="mb-1 fw-semibold">Manage Attendance</h5>
                            <p className="text-muted mb-0">
                                Monitor and manage employee attendance records, shifts, punch timings,
                                and attendance status across zones and wards.
                            </p>
                        </div>

                        <div className="d-flex flex-wrap gap-2 flex-shrink-0">
                            <button
                                type="button"
                                className="button-container"
                                onClick={() => {
                                    setEditAttendanceData(null);
                                    setIsEditAttendance(false);
                                    setShowAddModal(true);
                                }}
                            >
                                <FontAwesomeIcon icon={faPlus} className="me-1 icon-container" />
                                Add Attendance
                            </button>
                            <button
                                type="button"
                                className="button-container"
                                onClick={handleExportExcel}
                            >
                                <FontAwesomeIcon icon={faFileExcel} className="me-1 icon-container" /> Excel
                            </button>
                            <button
                                type="button"
                                className="button-container"
                                onClick={handleExportPDF}
                            >
                                <FontAwesomeIcon icon={faFilePdf} className="me-1 icon-container" /> PDF
                            </button>
                        </div>
                    </div>

                    {/* ── Filter Card ── */}
                    <div className="card shadow mb-3">
                        <div className="card-body">
                            <form onSubmit={handleApplyFilters}>
                                <Row className="align-items-end g-3">

                                    {/* Zone
                                    <Col xs="12" md="6" lg="3">
                                        <label className="form-label">Zone</label>
                                        <select
                                            name="ZoneId"
                                            className="form-select bg-white"
                                            value={filterData.ZoneId}
                                            onChange={handleChange}
                                        >
                                            {zoneOptions.map((z) => (
                                                <option key={z.zoneId ?? z.ZoneId} value={z.zoneId ?? z.ZoneId}>
                                                    {z.zoneName ?? z.ZoneName}
                                                </option>
                                            ))}
                                        </select>
                                    </Col> */}

                                    {/* Zone */}
                                    <Col xs="12" md="6" lg="3">
                                        <label className="form-label">Zone</label>

                                        <Select
                                            styles={{ control: (base) => ({ ...base, backgroundColor: "white" }) }}
                                            name="ZoneId"
                                            classNamePrefix="react-select"
                                            placeholder="Select Zone"
                                            isSearchable
                                            options={zoneOptions.map((z) => ({
                                                value: z.zoneId ?? z.ZoneId,
                                                label: z.zoneName ?? z.ZoneName,
                                            }))}
                                            value={
                                                zoneOptions
                                                    .map((z) => ({
                                                        value: z.zoneId ?? z.ZoneId,
                                                        label: z.zoneName ?? z.ZoneName,
                                                    }))
                                                    .find((option) => option.value === filterData.ZoneId) || null
                                            }
                                            onChange={(selectedOption) =>
                                                handleChange({
                                                    target: {
                                                        name: "ZoneId",
                                                        value: selectedOption ? selectedOption.value : "",
                                                    },
                                                })
                                            }
                                        />
                                    </Col>

                                    {/* Ward */}
                                    {/* <Col xs="12" md="6" lg="3">
                                        <label className="form-label">
                                            Ward
                                            {!filterData.ZoneId && (
                                                <span className="text-muted ms-1" style={{ fontSize: "11px" }}>
                                                    (select zone first)
                                                </span>
                                            )}
                                        </label>
                                        <select
                                            name="WardId"
                                            className="form-select bg-white"
                                            value={filterData.WardId}
                                            onChange={handleChange}
                                            disabled={!filterData.ZoneId}
                                        >
                                            <option value="">Select Ward</option>
                                            {wardOptions.map((w) => (
                                                <option key={w.circleId ?? w.wardId} value={w.circleId ?? w.wardId}>
                                                    {w.circleName ?? w.wardName}
                                                </option>
                                            ))}
                                        </select>
                                    </Col> */}
                                    <Col xs="12" md="6" lg="3">
                                        <label className="form-label">
                                            Ward
                                            {!filterData.ZoneId && (
                                                <span className="text-muted ms-1" style={{ fontSize: "11px" }}>
                                                    (select zone first)
                                                </span>
                                            )}
                                        </label>

                                        <Select
                                            styles={{ control: (base) => ({ ...base, backgroundColor: "white" }) }}
                                            name="WardId"
                                            classNamePrefix="react-select"
                                            placeholder="Select Ward"
                                            isSearchable
                                            isDisabled={!filterData.ZoneId}
                                            options={wardOptions.map((w) => ({
                                                value: w.circleId ?? w.wardId,
                                                label: w.circleName ?? w.wardName,
                                            }))}
                                            value={
                                                wardOptions
                                                    .map((w) => ({
                                                        value: w.circleId ?? w.wardId,
                                                        label: w.circleName ?? w.wardName,
                                                    }))
                                                    .find((option) => option.value === filterData.WardId) || null
                                            }
                                            onChange={(selectedOption) =>
                                                handleChange({
                                                    target: {
                                                        name: "WardId",
                                                        value: selectedOption ? selectedOption.value : "",
                                                    },
                                                })
                                            }
                                        />
                                    </Col>
                                    {/* Employee Search */}
                                    <Col xs="12" md="6" lg="3">
                                        <label className="form-label">Employee Name / Code</label>
                                        <input
                                            type="text"
                                            name="EmployeeSearch"
                                            className="form-control bg-white"
                                            value={filterData.EmployeeSearch}
                                            onChange={handleChange}
                                            placeholder="Enter employee name or code"
                                        />
                                    </Col>

                                    {/* Status */}
                                    {/* <Col xs="12" md="6" lg="3">
                                        <label className="form-label">Status</label>
                                        <select
                                            name="Status"
                                            className="form-select bg-white"
                                            value={filterData.Status}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Status</option>
                                            <option value="Present">Present</option>
                                            <option value="Absent">Absent</option>
                                            <option value="Late">Late</option>
                                        </select>
                                    </Col> */}

                                    <Col xs="12" md="6" lg="3">
                                        <label className="form-label">Status</label>

                                        <Select
                                            styles={{ control: (base) => ({ ...base, backgroundColor: "white" }) }}
                                            name="Status"
                                            classNamePrefix="react-select"
                                            placeholder="Select Status"
                                            isSearchable
                                            options={[
                                                { value: "Present", label: "Present" },
                                                { value: "Absent", label: "Absent" },
                                                { value: "Late", label: "Late" },
                                            ]}
                                            value={
                                                [
                                                    { value: "Present", label: "Present" },
                                                    { value: "Absent", label: "Absent" },
                                                    { value: "Late", label: "Late" },
                                                ].find((option) => option.value === filterData.Status) || null
                                            }
                                            onChange={(selectedOption) =>
                                                handleChange({
                                                    target: {
                                                        name: "Status",
                                                        value: selectedOption ? selectedOption.value : "",
                                                    },
                                                })
                                            }
                                            isClearable
                                        />
                                    </Col>

                                    {/* Shift */}
                                    {/* <Col xs="12" md="6" lg="3">
                                        <label className="form-label">Shift</label>
                                        <select
                                            name="ShiftId"
                                            className="form-select bg-white"
                                            value={filterData.ShiftId}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Shift</option>
                                            {shiftOptions
                                                .filter((s) => s.isActive)
                                                .map((s) => (
                                                    <option key={s.shiftId ?? s.ShiftId} value={s.shiftId ?? s.ShiftId}>
                                                        {s.shiftName ?? s.shiftName}
                                                    </option>
                                                ))}
                                        </select>
                                    </Col> */}
                                    <Col xs="12" md="6" lg="3">
                                        <label className="form-label">Shift</label>

                                        <Select
                                            styles={{ control: (base) => ({ ...base, backgroundColor: "white" }) }}
                                            name="ShiftId"
                                            classNamePrefix="react-select"
                                            placeholder="Select Shift"
                                            isSearchable
                                            isClearable
                                            options={shiftOptions
                                                .filter((s) => s.isActive)
                                                .map((s) => ({
                                                    value: s.shiftId ?? s.ShiftId,
                                                    label: s.shiftName ?? s.ShiftName,
                                                }))}
                                            value={
                                                shiftOptions
                                                    .filter((s) => s.isActive)
                                                    .map((s) => ({
                                                        value: s.shiftId ?? s.ShiftId,
                                                        label: s.shiftName ?? s.ShiftName,
                                                    }))
                                                    .find((option) => option.value === filterData.ShiftId) || null
                                            }
                                            onChange={(selectedOption) =>
                                                handleChange({
                                                    target: {
                                                        name: "ShiftId",
                                                        value: selectedOption ? selectedOption.value : "",
                                                    },
                                                })
                                            }
                                        />
                                    </Col>
                                    {/* Designation */}
                                    {/* <Col xs="12" md="6" lg="3">
                                        <label className="form-label">Designation</label>
                                        <select
                                            name="DesignationId"
                                            className="form-select bg-white"
                                            value={filterData.DesignationId}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Designation</option>
                                            {designationOptions
                                                .filter((d) => d.isActive)
                                                .map((d) => (
                                                    <option key={d.designationId} value={d.designationId}>
                                                        {d.designationName}
                                                    </option>
                                                ))}
                                        </select>
                                    </Col> */}

                                    <Col xs="12" md="6" lg="3">
                                        <label className="form-label">Designation</label>

                                        <Select
                                            styles={{ control: (base) => ({ ...base, backgroundColor: "white" }) }}
                                            name="DesignationId"
                                            classNamePrefix="react-select"
                                            placeholder="Select Designation"
                                            isSearchable
                                            isClearable
                                            options={designationOptions
                                                .filter((d) => d.isActive)
                                                .map((d) => ({
                                                    value: d.designationId,
                                                    label: d.designationName,
                                                }))}
                                            value={
                                                designationOptions
                                                    .filter((d) => d.isActive)
                                                    .map((d) => ({
                                                        value: d.designationId,
                                                        label: d.designationName,
                                                    }))
                                                    .find((option) => option.value === filterData.DesignationId) || null
                                            }
                                            onChange={(selectedOption) =>
                                                handleChange({
                                                    target: {
                                                        name: "DesignationId",
                                                        value: selectedOption ? selectedOption.value : "",
                                                    },
                                                })
                                            }
                                        />
                                    </Col>

                                    {/* From Date */}
                                    <Col xs="12" md="6" lg="3">
                                        <label className="form-label">From Date</label>
                                        <input
                                            type="date"
                                            name="FromDate"
                                            className="form-control bg-white"
                                            value={filterData.FromDate}
                                            max={
                                                filterData.ToDate &&
                                                    filterData.ToDate < getCurrentDate()
                                                    ? filterData.ToDate
                                                    : getCurrentDate()
                                            }
                                            onChange={handleChange}
                                        />
                                    </Col>

                                    {/* To Date */}
                                    <Col xs="12" md="6" lg="3">
                                        <label className="form-label">To Date</label>
                                        <input
                                            type="date"
                                            name="ToDate"
                                            className="form-control bg-white"
                                            value={filterData.ToDate}
                                            min={filterData.FromDate}
                                            max={getCurrentDate()}
                                            onChange={handleChange}
                                        />
                                    </Col>

                                    {/* Buttons */}
                                    <Col xs="12" className="d-flex flex-wrap gap-2 mt-1">
                                        <button type="submit" style={applyButtonStyle}>
                                            APPLY FILTERS
                                        </button>
                                        <button type="button" style={resetButtonStyle} onClick={handleReset}>
                                            RESET
                                        </button>
                                    </Col>
                                </Row>
                            </form>
                        </div>
                    </div>

                    {/* ── Attendance Table ── */}
                    <div className="card shadow">
                        <div className="card-body">
                            <div className="top-header text-center">
                                <span>Attendance List</span>
                            </div>
                            <div className="table-responsive">
                                <MUITable loading={isLoading}
                                    needSRNumber
                                    columns={attendanceColumns}
                                    rows={displayedAttendanceData}
                                    tableHeight={500}
                                    hasExpandButton
                                    expandContent={(row) => (
                                        <AttendancePunchHistoryTable
                                            row={row}
                                            subThStyle={expandTableThStyle}
                                            subTdStyle={expandTableTdStyle}
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            {/* ── Add / Edit Attendance Modal ── */}
            <AddAttendanceModal
                showModal={showAddModal}
                closeModal={() => {
                    setShowAddModal(false);
                    setEditAttendanceData(null);
                    setIsEditAttendance(false);
                }}
                editData={editAttendanceData}
                isEditAttendance={isEditAttendance}
                setIsEditAttendance={setIsEditAttendance}
                fetchAttendance={fetchAttendance}
                zoneOptions={zoneOptions}
                wardOptions={wardOptions}
                shiftOptions={shiftOptions}
            />
        </>
    );
};

export default AttendanceManagementInformation;

